import React from 'react';
import { Truck, Flame, Wine, Beer, IceCream, Sparkles } from 'lucide-react';

const ServicePresentation = () => {
  const services = [
    {
      id: 1,
      icon: Truck,
      count: "2x",
      name: "Food Trucky",
      description: "Mobilní kuchyně pro přípravu pokrmů na místě",
      image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/2td32q5c_food-truck-prives%281%29.jpg"
    },
    {
      id: 2,
      icon: Flame,
      count: "2x",
      name: "Live Cooking Stanoviště",
      description: "Živá příprava jídel před hosty",
      image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/7ne4quvg_290807044_434303445370941_8352434526447174514_n-3.jpg"
    },
    {
      id: 3,
      icon: Wine,
      count: "1x",
      name: "Bar Truck XXL",
      description: "Špičkově vybavený mobilní bar",
      image: "https://vseproakci.cz/wp-content/uploads/2025/06/kontaktfest0783.jpg"
    },
    {
      id: 4,
      icon: Beer,
      count: "2x",
      name: "Beer Truck",
      description: "Výčepní vůz jako na Oktoberfestu",
      image: "https://vseproakci.cz/wp-content/uploads/2025/01/vycepni-vuz-L-1-scaled.jpg"
    },
    {
      id: 5,
      icon: IceCream,
      count: "1x",
      name: "Sweet Truck",
      description: "Zmrzinovač italské výroby Frigomat",
      image: "https://vseproakci.cz/wp-content/uploads/2025/01/Snimek-obrazovky-2025-01-12-v-16.49.06.png"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-cyan-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2310b981' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-700 px-6 py-3 rounded-full mb-6">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold text-lg">Představení servisu</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Kompletní gastronomická zóna
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Vytvoříme pro vás <strong className="text-cyan-600">komplexní gastronomickou zónu</strong> s live cooking stanicemi, 
              food trucky a profesionálním barovým servisem. <strong>Festivalový zážitek</strong>, který hosté nerozeznají od top eventů.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.id}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-cyan-100"
                >
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden bg-gray-100">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 right-4 bg-cyan-600 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg">
                      {service.count}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-cyan-100 text-cyan-600 rounded-xl p-3 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {service.name}
                      </h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feature Highlights */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-3">
                <div className="text-5xl font-bold text-cyan-200">8x</div>
                <div className="text-lg font-medium">Mobilních gastro jednotek</div>
                <div className="text-cyan-100 text-sm">Food trucky, bary a výčepy</div>
              </div>
              
              <div className="space-y-3">
                <div className="text-5xl font-bold text-cyan-200">800+</div>
                <div className="text-lg font-medium">Porcí za 8 hodin</div>
                <div className="text-cyan-100 text-sm">Kapacita jednoho trucku</div>
              </div>
              
              <div className="space-y-3">
                <div className="text-5xl font-bold text-cyan-200">15+</div>
                <div className="text-lg font-medium">Let zkušeností</div>
                <div className="text-cyan-100 text-sm">S organizací velkých eventů</div>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/20 text-center">
              <p className="text-lg text-cyan-50 italic">
                "Profesionální zázemí, které udělá z vaší akce nezapomenutelný zážitek"
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicePresentation;
